// frontend/src/features/reports/windows/popupWindow.js
/**
 * Low-level native popup lifecycle for report windows. No React here - just the
 * DOM plumbing that lets a report render (via a React portal) into a separate OS
 * window the user can drag onto another monitor.
 */

/**
 * Open a same-origin blank popup and seed it with a portal root. Returns the
 * Window handle, or null if the browser blocked it (must be called inside a user
 * gesture to succeed).
 *
 * Default size carries the 1056px design-width report at a true 100% fit:
 * 1056 + 2x16px viewer gutters + ~32px of scrollbar/window chrome = 1120 wide.
 * Browsers clamp a popup to the available screen, so a small display just gets a
 * smaller window and the viewer's fit-to-width takes over - it still fills it.
 */
export function openReportPopup({ title, width = 1120, height = 900 } = {}) {
  const features = `popup=yes,width=${width},height=${height},scrollbars=yes,resizable=yes`;
  const win = window.open('', '_blank', features);
  if (!win) return null;
  try {
    const doc = win.document;
    doc.title = title || 'Report';
    if (!doc.getElementById('report-popup-root')) {
      const meta = doc.createElement('meta');
      meta.setAttribute('charset', 'utf-8');
      doc.head.appendChild(meta);
      doc.documentElement.style.height = '100%';
      doc.body.style.height = '100%';
      doc.body.style.margin = '0';
      const root = doc.createElement('div');
      root.id = 'report-popup-root';
      root.style.height = '100%';
      doc.body.appendChild(root);
    }
  } catch (e) {
    // A torn-down or cross-origin document - unusable as a portal target.
    return null;
  }
  return win;
}

/**
 * Copy the opener's styles into the popup so Tailwind + app CSS apply.
 *
 * Same-origin sheets are serialized from the already-loaded CSSOM into inline
 * <style> tags rather than cloned as <link> nodes: the about:blank popup
 * inherits the site CSP, and Gecko (Zen/Firefox) fails style-src 'self'
 * matching for stylesheet fetches in never-navigated popups - the prod popup
 * rendered fully unstyled (2026-07-24). Inline styles ride on 'unsafe-inline',
 * which the CSP allows, and need no network fetch at all. Cross-origin sheets
 * (cssRules throws, e.g. Google Fonts) fall back to an absolute-href <link>;
 * if a browser blocks that too, fonts degrade to the system stack.
 */
export function copyStylesToPopup(popupWin) {
  if (!popupWin || popupWin.closed) return;
  const doc = popupWin.document;
  doc.head.querySelectorAll('[data-report-popup-style]').forEach((n) => n.remove());
  Array.from(document.styleSheets).forEach((sheet) => {
    if (sheet.disabled) return;
    let cssText = null;
    try {
      cssText = Array.from(sheet.cssRules).map((r) => r.cssText).join('\n');
    } catch (e) {
      cssText = null; // cross-origin sheet - CSSOM is unreadable
    }
    let el;
    if (cssText !== null) {
      el = doc.createElement('style');
      el.textContent = cssText;
    } else if (sheet.href) {
      el = doc.createElement('link');
      el.rel = 'stylesheet';
      el.href = sheet.href; // .href is always absolute
    } else {
      return;
    }
    el.setAttribute('data-report-popup-style', '');
    doc.head.appendChild(el);
  });
}

/** Paint the popup body background to match the app shell (chrome fills the rest). */
export function setPopupBodyTheme(popupWin, darkMode) {
  if (!popupWin || popupWin.closed) return;
  popupWin.document.body.style.background = darkMode ? '#0f0f0f' : '#f3f4f6';
}

// Everything below is the PRINT plumbing: it moves the paginated page tree into
// the report's own OS window so window.print() previews over THAT window instead
// of the opener (home page).
const PRINT_CONTAINER_ID = 'report-print-popup-root';
const PRINT_STYLE_ATTR = 'data-report-print-popup-style';

// Popup-scoped @media print rule: hide the on-screen viewer, reveal only the
// moved page tree. Self-sufficient (does not lean on the opener CSS copied into
// the popup): `body > #id` matches the copied App.css catch-all
// `body > *:not(#report-print-root)` in specificity and, appended last, wins on
// source order. @page mirrors App.css so the browser adds no margins on top of
// paged.js's already-margined landscape pages.
const POPUP_PRINT_CSS = `
@media print {
  html, body { height: auto !important; overflow: visible !important; }
  /* WHITE PAPER, EVERY SHEET. setPopupBodyTheme paints the app-shell theme onto the
     popup's body as an INLINE background (#0f0f0f in dark mode - the DEFAULT theme).
     <html> carries no background, so that inline colour propagates to the print
     CANVAS, which is painted on EVERY sheet; meanwhile paged.js's own print base CSS
     pins \`body { max-height: 100% }\` (one page box, and nothing overrides
     max-height), so the element-level white below covers sheet 1 ONLY. Every report
     therefore printed sheet 1 correctly and sheets 2+ black - with the report text
     forced to #000 by pagedReportCss, black-on-black left only the red/green cells
     and page numbers legible. Repainting the canvas white fixes all sheets at once;
     !important is required to outrank the inline shell colour. */
  html, body { background: #fff !important; }
  body > #report-popup-root { display: none !important; }
  body > #${PRINT_CONTAINER_ID} {
    display: block !important;
    position: static !important;
    left: auto !important;
    top: auto !important;
    width: auto !important;
    background: #fff;
  }
  #${PRINT_CONTAINER_ID} .pagedjs_page {
    box-shadow: none !important;
    margin: 0 !important;
    break-after: page;
    overflow: visible !important;
  }
  #${PRINT_CONTAINER_ID} [data-export-skip] { display: none !important; }
  @page { size: Letter landscape; margin: 0; }
}
`;

/**
 * Relocate the paginated pages from the opener's #report-print-root into a print
 * container in the POPUP document, and clone the paged.js styles (base :root vars
 * + .pagedjs_* classes + our pagedReportCss content rules) plus a popup-scoped
 * print rule into the popup head. paged.js CLONES its content into the pages, so
 * adopting its OUTPUT leaves the React print sources in the opener untouched.
 * Pair with cleanupPopupPrint after printing.
 */
export function movePrintPagesToPopup(printRoot, popupWin) {
  if (!popupWin || popupWin.closed) return;
  const doc = popupWin.document;
  const container = doc.createElement('div');
  container.id = PRINT_CONTAINER_ID;
  // Parked offscreen until @media print pulls it into flow (matches the opener's
  // #report-print-root), so it never flashes on screen.
  container.style.cssText = 'position:fixed;left:-99999px;top:0;width:1056px;';
  Array.from(printRoot.childNodes).forEach((node) => container.appendChild(doc.adoptNode(node)));
  doc.body.appendChild(container);
  // Serialize from the live CSSOM (not textContent) so the @page rules paginate.js
  // stripped after layout stay stripped; fall back to textContent when the sheet
  // is unreadable (jsdom). Same idiom as copyStylesToPopup.
  document.head.querySelectorAll('style[data-pagedjs-inserted-styles]').forEach((styleEl) => {
    let css = null;
    try { css = Array.from(styleEl.sheet.cssRules).map((r) => r.cssText).join('\n'); } catch (e) { css = null; }
    if (!css) css = styleEl.textContent;
    const clone = doc.createElement('style');
    clone.setAttribute(PRINT_STYLE_ATTR, '');
    clone.textContent = css;
    doc.head.appendChild(clone);
  });
  const printCss = doc.createElement('style');
  printCss.setAttribute(PRINT_STYLE_ATTR, '');
  printCss.textContent = POPUP_PRINT_CSS;
  doc.head.appendChild(printCss);
}

/** Remove the print container + injected styles from the popup document (idempotent). */
export function cleanupPopupPrint(popupWin) {
  if (!popupWin || popupWin.closed) return;
  const doc = popupWin.document;
  const container = doc.getElementById(PRINT_CONTAINER_ID);
  if (container) container.remove();
  doc.head.querySelectorAll(`[${PRINT_STYLE_ATTR}]`).forEach((n) => n.remove());
}

/**
 * Invoke `onClose` exactly once when the popup is closed (OS close button, or the
 * user navigates it away). Polls `closed` (reliable across browsers) and also
 * listens for the popup's pagehide as a fast path. Returns a stop function.
 */
export function watchPopupClose(popupWin, onClose) {
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    clearInterval(timer);
    try { popupWin.removeEventListener('pagehide', finish); } catch (e) { /* ignore */ }
    onClose();
  };
  const timer = setInterval(() => {
    if (!popupWin || popupWin.closed) finish();
  }, 500);
  try { popupWin.addEventListener('pagehide', finish); } catch (e) { /* ignore */ }
  return () => {
    done = true;
    clearInterval(timer);
    try { popupWin.removeEventListener('pagehide', finish); } catch (e) { /* ignore */ }
  };
}
